/**
 * iDiamonds — serviciu de gravură pentru inelele de logodnă.
 *
 * 1. Bifa "Adaugă gravură" arată/ascunde câmpul de text (obligatoriu când e bifat).
 * 2. La submit-ul formularului de produs (capture, ca în custom-ring-size) blochează
 *    adăugarea în coș dacă gravura e bifată dar textul lipsește.
 * 3. După ce inelul intră în coș (eveniment `product-ajax:added`), adaugă automat
 *    produsul-taxă de gravură ca linie separată, cu textul și inelul ca proprietăți.
 */
class CustomEngraving extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('.custom-engraving__checkbox');
    this.field = this.querySelector('.custom-engraving__field');
    this.textInput = this.querySelector('.custom-engraving__text');
    this.errorEl = this.querySelector('.custom-engraving__error');
    this.form = document.getElementById(this.dataset.formId);
    this.variantId = this.dataset.variantId;
    this.productTitle = this.dataset.productTitle;
    this.requiredMessage = this.dataset.requiredMessage;

    if (!this.checkbox || !this.form || !this.variantId) return;

    this.pendingEngraving = false;

    this._onCheckboxChange = this._onCheckboxChange.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onProductAdded = this._onProductAdded.bind(this);

    this.checkbox.addEventListener('change', this._onCheckboxChange);
    this.textInput.addEventListener('input', () => this._clearError());
    this.form.addEventListener('submit', this._onSubmit, true);
    document.addEventListener('product-ajax:added', this._onProductAdded);
  }

  disconnectedCallback() {
    if (this.form) this.form.removeEventListener('submit', this._onSubmit, true);
    document.removeEventListener('product-ajax:added', this._onProductAdded);
  }

  _onCheckboxChange() {
    const on = this.checkbox.checked;
    this.field.hidden = !on;
    this.textInput.disabled = !on;
    if (on) {
      this.textInput.focus();
    } else {
      this._clearError();
    }
  }

  _onSubmit(event) {
    // Resetăm la fiecare încercare de adăugare în coș.
    this.pendingEngraving = false;
    if (!this.checkbox.checked) return;

    const text = (this.textInput.value || '').trim();
    if (!text) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.field.hidden = false;
      this.textInput.disabled = false;
      this._showError();
      this.textInput.focus();
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Valid: reținem ca, după ce inelul intră în coș, să adăugăm taxa de gravură.
    this.pendingEngraving = true;
    this.engravingText = text;
    this.engravingQuantity = this._getQuantity();
    const parentIdInput = this.form.querySelector('[name="id"]');
    this.parentVariantId = parentIdInput ? String(parentIdInput.value) : '';
  }

  _getQuantity() {
    const qtyInput = this.form.querySelector('[name="quantity"]');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  _onProductAdded() {
    if (!this.pendingEngraving) return;
    this.pendingEngraving = false;
    this._addEngravingFee(this.engravingText, this.engravingQuantity, this.parentVariantId);
  }

  async _addEngravingFee(text, quantity, parentVariantId) {
    const properties = {
      'Gravură': text,
      'Pentru produsul': this.productTitle,
    };
    if (parentVariantId) properties._pentru_varianta = parentVariantId;

    try {
      await window.iDiamonds.linkedItems.add({ variantId: this.variantId, quantity, properties });
      // Resetăm pentru o eventuală adăugare următoare.
      this.checkbox.checked = false;
      this._onCheckboxChange();
      this.textInput.value = '';
    } catch (error) {
      console.error('Gravură — eroare la adăugare:', error);
    }
  }

  _showError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = this.requiredMessage;
    this.errorEl.hidden = false;
    this.classList.add('custom-engraving--error');
  }

  _clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.classList.remove('custom-engraving--error');
  }
}

if (!customElements.get('custom-engraving')) {
  customElements.define('custom-engraving', CustomEngraving);
}
